'use client';

import { type PropsWithChildren, useMemo } from 'react';

import { AuthStatusEnum, useAuth } from '@sola/auth-sdk/store.tsx';
import type { VisibilityRule } from '@sola/schema/lib/schema.ts';

import { evaluateVisibilityRules } from '../utils/visibility-rules.ts';

/**
 * Client-side visibility gate for auth-dependent rules (loggedIn, knownSupporter).
 *
 * While auth is resolving (InProgress), children are rendered to avoid layout
 * shift and hydration mismatches. Once auth settles, rules are evaluated and
 * content is hidden/shown accordingly.
 */
export const VisibilityGate = ({
  rules,
  experiments,
  children,
}: PropsWithChildren<{
  rules: VisibilityRule[];
  experiments?: string[];
}>) => {
  const { currentStatus, authUser } = useAuth('currentStatus', 'authUser');

  const loggedIn = currentStatus === AuthStatusEnum.Authenticated;
  const knownSupporter = authUser?.supporter?.status === 'Active';

  const shouldHide = useMemo(
    () =>
      evaluateVisibilityRules(rules, { experiments, loggedIn, knownSupporter }),
    [rules, experiments, loggedIn, knownSupporter],
  );

  // While auth is still resolving, render children to avoid layout shift.
  if (currentStatus === AuthStatusEnum.InProgress) {
    return <>{children}</>;
  }

  if (shouldHide) {
    return null;
  }

  return <>{children}</>;
};
